// Settings → Approved Counties — the master list of where AHFC is approved to
// serve, maintained here by admins (2026-10-20).
//
// Every screen that asks "is this client in the approved area?" reads this
// list through lib/serviceArea.js: the client profile, the client form, the
// Clients filter, the dashboard card and the county map. Nothing stores the
// answer on a client, so adding a county here moves every client in it into
// the approved area at once, and removing one moves them out.
//
// Internal only. The table cannot be read by anonymous requests, and nothing
// on this page is ever shown to families, referrers, agencies or applicants.
//
// A county is never deleted: removing it records when, by whom and why, so the
// approved area on any past date can still be told.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase, lastFirst } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { parseLocalDate } from '../../lib/dateUtils';
import { isReceivingService } from '../../lib/serviceModel';
import {
  loadApprovedCountyRows, liveCounties, addableCounties, serviceAreaStatus,
  addApprovedCounty, updateApprovedCounty, removeApprovedCounty, clearServiceAreaCache,
} from '../../lib/serviceArea';
import Modal from '../../components/Modal';

const fmtDate = (d) => { try { return d ? format(parseLocalDate(d), 'MMM d, yyyy') : '—'; } catch { return '—'; } };
const th = { textAlign: 'left', fontSize: 10.5, color: 'var(--text3)', fontWeight: 500, padding: '8px 10px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '9px 10px', borderBottom: '0.5px solid var(--border)', fontSize: 13, verticalAlign: 'top' };
const EMPTY_ADD = { county: '', approvedOn: '', reference: '', documentUrl: '', notes: '' };

export default function ApprovedCounties() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [add, setAdd] = useState(EMPTY_ADD);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // row being edited
  const [removing, setRemoving] = useState(null); // row being removed
  const [reason, setReason] = useState('');
  const [modalError, setModalError] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    clearServiceAreaCache();
    const [{ data, error: e1 }, { data: cl, error: e2 }] = await Promise.all([
      loadApprovedCountyRows(),
      supabase.from('clients').select('id, client_id, first_name, last_name, county, current_status').eq('is_test', false),
    ]);
    if (e1 || e2) setError((e1 || e2).message || 'Could not load the approved counties.');
    setRows(data || []);
    setClients((cl || []).filter(isReceivingService));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const live = useMemo(() => rows.filter(r => !r.removed_on), [rows]);
  const removed = useMemo(() => rows.filter(r => r.removed_on).sort((a, b) => String(b.removed_on).localeCompare(String(a.removed_on))), [rows]);
  const liveNames = useMemo(() => liveCounties(rows), [rows]);
  const addable = useMemo(() => addableCounties(liveNames), [liveNames]);
  const countIn = (county) => clients.filter(c => String(c.county || '').trim().toLowerCase() === county.toLowerCase()).length;
  const outside = useMemo(() => clients
    .map(c => ({ ...c, status: serviceAreaStatus(c.county, liveNames) }))
    .filter(c => c.status !== 'approved')
    .sort((a, b) => lastFirst(a.first_name, a.last_name).localeCompare(lastFirst(b.first_name, b.last_name))), [clients, liveNames]);

  const doAdd = async () => {
    if (!add.county) return;
    setBusy(true); setError('');
    const { error: e } = await addApprovedCounty({ ...add, profile });
    setBusy(false);
    if (e) { setError(e.message); return; }
    setAdd(EMPTY_ADD);
    load();
  };

  const doEdit = async () => {
    setBusy(true); setModalError('');
    const { error: e } = await updateApprovedCounty({
      id: editing.id, approvedOn: editing.approved_on, reference: editing.reference,
      documentUrl: editing.document_url, notes: editing.notes,
    });
    setBusy(false);
    if (e) { setModalError(e.message); return; }
    setEditing(null);
    load();
  };

  const doRemove = async () => {
    setBusy(true); setModalError('');
    const { error: e } = await removeApprovedCounty({ id: removing.id, county: removing.county, reason, profile });
    setBusy(false);
    if (e) { setModalError(e.message); return; }
    setRemoving(null); setReason('');
    load();
  };

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 600 }}>Approved Counties</h2>
        <div style={{ fontSize: 12.5, color: 'var(--text3)', lineHeight: 1.6 }}>
          The counties AHFC is approved to serve. Every client's service area is worked out from this list and
          their county — add a county here when it is approved and its clients move into the approved area at once.
          Internal only: never shown to families, referrers, agencies or applicants.
        </div>
      </div>

      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', color: 'var(--danger)', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, marginBottom: 12 }}>{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Add a county</div></div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">County *</label>
            <select className="form-select" value={add.county} onChange={e => setAdd(a => ({ ...a, county: e.target.value }))}>
              <option value="">— Choose —</option>
              {addable.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Approved on</label>
            <input type="date" className="form-input" value={add.approvedOn} onChange={e => setAdd(a => ({ ...a, approvedOn: e.target.value }))} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Reference</label>
            <input className="form-input" placeholder="DCH letter or number" value={add.reference} onChange={e => setAdd(a => ({ ...a, reference: e.target.value }))} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Approval letter link</label>
            <input className="form-input" placeholder="Drive link (optional)" value={add.documentUrl} onChange={e => setAdd(a => ({ ...a, documentUrl: e.target.value }))} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Notes</label>
            <input className="form-input" value={add.notes} onChange={e => setAdd(a => ({ ...a, notes: e.target.value }))} />
          </div>
          <button className="btn btn-primary" disabled={!add.county || busy} onClick={doAdd}>{busy ? 'Saving…' : 'Add county'}</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Approved · {live.length}</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {loading ? <div style={{ padding: 16, fontSize: 13, color: 'var(--text3)' }}>Loading…</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr><th style={th}>County</th><th style={th}>Approved on</th><th style={th}>Reference</th><th style={th}>Active clients</th><th style={th}>Added</th><th style={th} /></tr>
              </thead>
              <tbody>
                {live.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.county}</td>
                    <td style={td}>{fmtDate(r.approved_on)}</td>
                    <td style={td}>
                      {r.reference || <span style={{ color: 'var(--text3)' }}>—</span>}
                      {r.document_url && <> · <a href={r.document_url} target="_blank" rel="noopener noreferrer">letter</a></>}
                      {r.notes && <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>{r.notes}</div>}
                    </td>
                    <td style={td}>{countIn(r.county)}</td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--text3)' }}>{r.added_by_name || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setModalError(''); setEditing({ ...r }); }}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => { setModalError(''); setReason(''); setRemoving(r); }}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Active clients outside the approved area · {outside.length}</div></div>
        <div className="card-body" style={{ fontSize: 13 }}>
          {outside.length === 0 ? <span style={{ color: 'var(--text3)' }}>Every active client is in an approved county.</span> : (
            <div style={{ display: 'grid', gap: 6 }}>
              {outside.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Link to={`/clients/${c.id}`}>{c.client_id} {lastFirst(c.first_name, c.last_name)}</Link>
                  <span style={{ color: 'var(--text3)' }}>
                    {c.status === 'none' ? 'No county recorded' : c.status === 'unrecognised' ? `"${c.county}" — not a Georgia county` : c.county}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {removed.length > 0 && (
        <div className="card">
          <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowRemoved(v => !v)}>
            <div className="card-title">Removed · {removed.length}</div>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{showRemoved ? 'Hide' : 'Show'}</span>
          </div>
          {showRemoved && (
            <div className="card-body" style={{ display: 'grid', gap: 6, fontSize: 13 }}>
              {removed.map(r => (
                <div key={r.id}><b>{r.county}</b> <span style={{ color: 'var(--text3)' }}>· removed {fmtDate(r.removed_on)}{r.removed_by_name ? ` by ${r.removed_by_name}` : ''} — {r.removed_reason}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal show={!!editing} onClose={() => setEditing(null)} title={editing ? `Edit ${editing.county}` : ''} error={modalError}
        footer={<><button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={doEdit}>{busy ? 'Saving…' : 'Save'}</button></>}>
        {editing && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="form-group" style={{ margin: 0 }}><label className="form-label">Approved on</label><input type="date" className="form-input" value={editing.approved_on || ''} onChange={e => setEditing(x => ({ ...x, approved_on: e.target.value }))} /></div>
            <div className="form-group" style={{ margin: 0 }}><label className="form-label">Reference</label><input className="form-input" value={editing.reference || ''} onChange={e => setEditing(x => ({ ...x, reference: e.target.value }))} /></div>
            <div className="form-group" style={{ margin: 0 }}><label className="form-label">Approval letter link</label><input className="form-input" value={editing.document_url || ''} onChange={e => setEditing(x => ({ ...x, document_url: e.target.value }))} /></div>
            <div className="form-group" style={{ margin: 0 }}><label className="form-label">Notes</label><input className="form-input" value={editing.notes || ''} onChange={e => setEditing(x => ({ ...x, notes: e.target.value }))} /></div>
          </div>
        )}
      </Modal>

      <Modal show={!!removing} onClose={() => setRemoving(null)} title={removing ? `Remove ${removing.county}?` : ''} error={modalError}
        footer={<><button className="btn btn-outline" onClick={() => setRemoving(null)}>Cancel</button><button className="btn btn-primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }} disabled={busy || !reason.trim()} onClick={doRemove}>{busy ? 'Removing…' : 'Remove county'}</button></>}>
        {removing && (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              {countIn(removing.county)} active client{countIn(removing.county) === 1 ? '' : 's'} in {removing.county} will show as outside the approved area.
              The county stays in the history below, with this reason.
            </p>
            <div className="form-group" style={{ margin: 0 }}><label className="form-label">Reason *</label><input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Approval withdrawn by DCH" /></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
